import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDownUp, Loader2, AlertCircle } from "lucide-react";
import { userApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate, newRequestId } from "@/lib/format";
import { PageHeader, StatusBadge, useDebounced } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Exchange() {
  const qc = useQueryClient();
  const [from, setFrom] = useState("INR");
  const [amount, setAmount] = useState("");
  const [rot, setRot] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const to = from === "INR" ? "USDT" : "INR";
  const walletsQ = useQuery({ queryKey: ["wallets"], queryFn: () => userApi.get("/wallets").then((r) => r.data) });
  const exQ = useQuery({ queryKey: ["exchanges"], queryFn: () => userApi.get("/exchanges").then((r) => r.data) });
  const bal = walletsQ.data?.find((w) => w.currency === from)?.balance ?? 0;
  const amt = parseFloat(amount);
  const dAmt = useDebounced(amt > 0 ? amt : 0, 300);
  const quoteQ = useQuery({ queryKey: ["quote", from, dAmt], queryFn: () => userApi.post("/exchange/quote", { from_currency: from, amount: dAmt }).then((r) => r.data), enabled: dAmt > 0 });
  const rateQ = useQuery({ queryKey: ["quote", from, 1], queryFn: () => userApi.post("/exchange/quote", { from_currency: from, amount: from === "INR" ? 1000 : 10 }).then((r) => r.data) });
  const q = quoteQ.data;
  const insufficient = amt > bal;
  const min = rateQ.data?.min_amount;
  const belowMin = amt > 0 && min && amt < min;
  const can = amt > 0 && !insufficient && !belowMin && q && !busy;

  const swap = () => { setFrom(to); setAmount(""); setRot(rot + 180); setErr(""); };
  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await userApi.post("/exchange", { from_currency: from, amount: amt, client_request_id: newRequestId() });
      toast.success("Exchange completed", { description: `Received ${fmtMoney(r.data.receive_amount, r.data.to_currency)}` });
      setAmount("");
      qc.invalidateQueries({ queryKey: ["wallets"] });
      qc.invalidateQueries({ queryKey: ["exchanges"] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Exchange" subtitle="Convert instantly between your INR and USDT wallets." />
      <div className="scene-3d grid grid-cols-1 gap-5 lg:grid-cols-12">
        <Card className="card-3d lift-on-hover lg:col-span-7">
          <CardContent className="space-y-3 p-5 sm:p-6">
            <div className="rounded-xl border bg-secondary p-4">
              <div className="flex justify-between text-xs text-muted-foreground"><span>You pay</span><button className="font-medium text-primary" onClick={() => setAmount(String(bal))} data-testid="exchange-max-button">Balance: <span className="num">{fmtMoney(bal, from)}</span></button></div>
              <div className="mt-2 flex items-center gap-3">
                <Input inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))} className="num h-14 border-0 bg-white text-2xl" data-testid="exchange-from-amount-input" />
                <span className="rounded-lg bg-white px-3 py-2 text-sm font-semibold" data-testid="exchange-from-currency">{from}</span>
              </div>
            </div>
            <div className="flex justify-center">
              <Button variant="outline" size="icon" className="rounded-full bg-white" onClick={swap} aria-label="Swap direction" data-testid="exchange-swap-direction-button">
                <ArrowDownUp className="h-4 w-4 transition-transform duration-300" style={{ transform: `rotate(${rot}deg)` }} />
              </Button>
            </div>
            <div className="rounded-xl border p-4">
              <p className="text-xs text-muted-foreground">You receive</p>
              <div className="mt-2 flex items-center gap-3">
                <p className="num flex-1 text-2xl font-semibold" data-testid="exchange-receive-amount">{quoteQ.isFetching ? <Skeleton className="h-8 w-40" /> : q && amt > 0 ? fmtMoney(q.receive_amount, to, { noUnit: true }) : "0.00"}</p>
                <span className="rounded-lg bg-secondary px-3 py-2 text-sm font-semibold">{to}</span>
              </div>
            </div>
            <div className="space-y-1.5 rounded-lg bg-secondary px-4 py-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Rate</span><span className="num" data-testid="exchange-rate">1 USDT = {fmtMoney(rateQ.data?.rate, "INR")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fee</span><span className="num">{rateQ.data?.fee_pct ?? 0}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Minimum</span><span className="num">{min ? fmtMoney(min, from) : "—"}</span></div>
            </div>
            {insufficient && <p className="text-sm text-rose-700" data-testid="exchange-insufficient-error">Insufficient {from} balance.</p>}
            {belowMin && <p className="text-sm text-rose-700">Minimum exchange is {fmtMoney(min, from)}.</p>}
            {err && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription data-testid="exchange-error">{err}</AlertDescription></Alert>}
            <Button className="btn-lift h-12 w-full text-base" disabled={!can} onClick={submit} data-testid="exchange-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Exchange {from} → {to}</Button>
          </CardContent>
        </Card>
        <Card className="card-3d lift-on-hover lg:col-span-5">
          <CardHeader className="pb-2"><CardTitle className="text-base">Recent exchanges</CardTitle></CardHeader>
          <CardContent>
            {exQ.data?.length === 0 && <p className="text-sm text-muted-foreground">No exchanges yet.</p>}
            <ul className="divide-y">
              {exQ.data?.map((e) => (
                <li key={e.id} className="flex items-center gap-3 py-3" data-testid="exchange-history-item">
                  <div className="min-w-0 flex-1">
                    <p className="num text-sm font-medium">{fmtMoney(e.amount, e.from_currency)} → {fmtMoney(e.receive_amount, e.to_currency)}</p>
                    <p className="text-xs text-muted-foreground">{e.id} · {fmtDate(e.created_at)}</p>
                  </div>
                  <StatusBadge status={e.status} />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
